<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import CampaignReport from 'dashboard/components-next/Campaigns/Report/CampaignReport.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useCampaignReport } from 'dashboard/composables/useCampaignReport';
import CampaignsAPI from 'dashboard/api/campaigns';
import CampaignAbTestsAPI from 'dashboard/api/campaignAbTests';

const props = defineProps({
  listRouteName: {
    type: String,
    required: true,
  },
  campaignParam: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const showAlert = message => useAlert(message);

const campaigns = useMapGetter('campaigns/getAllCampaigns');
const isPausingOrResuming = ref(false);
const isRetrying = ref(false);
const isExporting = ref(false);
const isValidating = ref(false);
const stats = ref(null);
const showRetryConfirm = ref(false);

const campaignReportRef = ref(null);

const { reportData, isLoadingReport, loadReport, resetReport } =
  useCampaignReport();

const campaignParam = computed(() => props.campaignParam?.toString() || '');

const slugify = value =>
  value
    ?.toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || '';

const paramId = computed(() => {
  const match = campaignParam.value.match(/^\d+/);
  return match ? match[0] : null;
});

const paramSlug = computed(() => {
  const slugPart = campaignParam.value.replace(/^\d+-?/, '');
  return slugPart || null;
});

const campaignById = computed(() => {
  if (!paramId.value) return null;
  const list = campaigns.value || [];
  return list.find(campaign => String(campaign.id) === paramId.value) || null;
});

const campaignBySlug = computed(() => {
  if (!paramSlug.value) return null;
  const list = campaigns.value || [];
  return (
    list.find(campaign => slugify(campaign.title) === paramSlug.value) || null
  );
});

const activeCampaign = computed(() => {
  return campaignById.value || campaignBySlug.value || null;
});

const requestCampaignId = computed(() => {
  if (paramId.value) return paramId.value;
  return activeCampaign.value ? activeCampaign.value.id : null;
});

const summary = computed(() => ({
  sent: reportData.value?.sent ?? activeCampaign.value?.it_sent ?? 0,
  failed: reportData.value?.failed ?? activeCampaign.value?.failed ?? 0,
  // Sem isso o card "Na fila" mostrava sempre 0 (UNITEC 02/10: 80 na fila).
  pending: reportData.value?.pending ?? 0,
  processed:
    reportData.value?.processed ?? activeCampaign.value?.status_send ?? 0,
}));

const contacts = computed(() => reportData.value?.contacts ?? []);

const hasData = computed(() => !!activeCampaign.value || !!reportData.value);

const ensureCampaignsLoaded = async () => {
  if (!campaigns.value || campaigns.value.length === 0) {
    await store.dispatch('campaigns/get');
  }
};

watch(
  requestCampaignId,
  id => {
    if (id) {
      loadReport(id);
    } else {
      resetReport();
    }
  },
  { immediate: true }
);

watch(
  () => route.params.accountId,
  () => {
    ensureCampaignsLoaded();
  },
  { immediate: true }
);

onMounted(() => {
  ensureCampaignsLoaded();
});

onBeforeUnmount(() => {
  resetReport();
});

const goBack = () => {
  router.push({
    name: props.listRouteName,
    params: {
      accountId: route.params.accountId,
    },
  });
};

const handleClose = () => {
  goBack();
};

const showMissingState = computed(
  () => !isLoadingReport.value && !hasData.value
);

const isRunning = computed(
  () => activeCampaign.value?.campaign_status === 'running'
);
const isPaused = computed(
  () => activeCampaign.value?.campaign_status === 'paused'
);
const hasCredits = computed(() => {
  const c = activeCampaign.value;
  return c && (c.total_credits_reserved > 0 || c.total_credits_used > 0);
});

const creditInfo = computed(() => {
  const c = activeCampaign.value;
  if (!c) return null;
  return {
    reserved: c.total_credits_reserved || 0,
    used: c.total_credits_used || 0,
    refunded: c.total_credits_refunded || 0,
  };
});

const pauseCampaign = async () => {
  isPausingOrResuming.value = true;
  try {
    await store.dispatch('campaigns/pauseCampaign', activeCampaign.value.id);
    showAlert(t('CAMPAIGN.REPORT.PAUSED'));
    await store.dispatch('campaigns/get');
  } catch {
    showAlert(t('CAMPAIGN.REPORT.PAUSE_ERROR'));
  } finally {
    isPausingOrResuming.value = false;
  }
};

const resumeCampaign = async () => {
  isPausingOrResuming.value = true;
  try {
    await store.dispatch('campaigns/resumeCampaign', activeCampaign.value.id);
    showAlert(t('CAMPAIGN.REPORT.RESUMED'));
    await store.dispatch('campaigns/get');
  } catch {
    showAlert(t('CAMPAIGN.REPORT.RESUME_ERROR'));
  } finally {
    isPausingOrResuming.value = false;
  }
};

// Stats
const loadStats = async () => {
  const id = requestCampaignId.value;
  if (!id) return;
  try {
    const { data } = await CampaignsAPI.getStats(id);
    stats.value = data;
  } catch {
    // Stats are non-critical, silent fail
  }
};

// Resultados do Teste A/B (variantes + métricas por ACK). O endpoint sempre
// existiu, mas nenhuma tela consumia — o teste rodava às cegas.
const abStats = ref(null);
const loadAbStats = async () => {
  const id = requestCampaignId.value;
  if (!id) return;
  try {
    const { data } = await CampaignAbTestsAPI.getAll(id);
    const test = (data || [])[0];
    if (!test) {
      abStats.value = null;
      return;
    }
    const { data: testStats } = await CampaignAbTestsAPI.getStats(id, test.id);
    abStats.value = testStats;
  } catch {
    abStats.value = null;
  }
};

const failedCount = computed(
  () => stats.value?.failed ?? summary.value?.failed ?? 0
);
const hasFailedMessages = computed(() => failedCount.value > 0);

// Retry failed
const retryFailed = async () => {
  showRetryConfirm.value = false;
  if (!activeCampaign.value) return;
  isRetrying.value = true;
  try {
    const { data } = await CampaignsAPI.retryFailed(activeCampaign.value.id);
    showAlert(t('CAMPAIGN.REPORT.RETRY.SUCCESS', { count: data.retried }));
    await Promise.all([
      loadReport(requestCampaignId.value, { force: true }),
      loadStats(),
      store.dispatch('campaigns/get'),
    ]);
  } catch {
    showAlert(t('CAMPAIGN.REPORT.RETRY.ERROR'));
  } finally {
    isRetrying.value = false;
  }
};

// Retry individual contact
const retryContact = async contact => {
  if (!activeCampaign.value || !contact?.id) return;
  try {
    await CampaignsAPI.retryContact(activeCampaign.value.id, contact.id);
    showAlert(
      t('CAMPAIGN.REPORT.RETRY.CONTACT_SUCCESS', {
        name: contact.name || contact.phone_number,
      })
    );
    await Promise.all([
      loadReport(requestCampaignId.value, { force: true }),
      loadStats(),
    ]);
  } catch {
    showAlert(t('CAMPAIGN.REPORT.RETRY.CONTACT_ERROR'));
  } finally {
    campaignReportRef.value?.clearRetrying(contact.id);
  }
};

// Validate numbers
const validateNumbers = async () => {
  if (!activeCampaign.value) return;
  isValidating.value = true;
  try {
    await CampaignsAPI.validateNumbers(activeCampaign.value.id);
    showAlert(t('CAMPAIGN.REPORT.VALIDATE.STARTED'));
  } catch {
    showAlert(t('CAMPAIGN.REPORT.VALIDATE.ERROR'));
  } finally {
    isValidating.value = false;
  }
};

// Export server-side CSV
const exportCsv = async () => {
  isExporting.value = true;
  try {
    const response = await CampaignsAPI.exportCsv(activeCampaign.value.id);
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `campaign-${activeCampaign.value.id}-export.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch {
    showAlert(t('CAMPAIGN.REPORT.EXPORT.ERROR'));
  } finally {
    isExporting.value = false;
  }
};

watch(requestCampaignId, id => {
  if (id) {
    loadStats();
    loadAbStats();
  }
});

onMounted(() => {
  if (requestCampaignId.value) {
    loadStats();
    loadAbStats();
  }
});
</script>

<template>
  <section class="flex flex-col h-full bg-n-background">
    <header class="px-4 sm:px-6 py-4 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-arrow-left"
            :aria-label="t('CAMPAIGN.REPORT.ACTIONS.BACK')"
            @click="goBack"
          >
            <span class="hidden sm:inline">{{
              t('CAMPAIGN.REPORT.ACTIONS.BACK')
            }}</span>
          </Button>
          <h1 class="text-lg font-medium text-n-slate-12">
            {{ t('CAMPAIGN.REPORT.PAGE_TITLE') }}
          </h1>
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="faded"
            color="blue"
            size="sm"
            icon="i-lucide-phone-call"
            :loading="isValidating"
            @click="validateNumbers"
          >
            {{ t('CAMPAIGN.REPORT.VALIDATE.BUTTON') }}
          </Button>
          <Button
            v-if="hasFailedMessages"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-refresh-cw"
            :loading="isRetrying"
            @click="showRetryConfirm = true"
          >
            {{ t('CAMPAIGN.REPORT.RETRY.BUTTON') }}
            <span class="ml-1 text-xs opacity-75">({{ failedCount }})</span>
          </Button>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-download"
            :loading="isExporting"
            @click="exportCsv"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.BUTTON') }}
          </Button>
          <Button
            v-if="isRunning"
            variant="faded"
            color="amber"
            size="sm"
            :loading="isPausingOrResuming"
            @click="pauseCampaign"
          >
            {{ t('CAMPAIGN.REPORT.ACTIONS.PAUSE') }}
          </Button>
          <Button
            v-if="isPaused"
            variant="faded"
            color="teal"
            size="sm"
            :loading="isPausingOrResuming"
            @click="resumeCampaign"
          >
            {{ t('CAMPAIGN.REPORT.ACTIONS.RESUME') }}
          </Button>
        </div>
      </div>
    </header>
    <main class="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
      <div v-if="isLoadingReport && !hasData" class="flex justify-center py-10">
        <Spinner />
      </div>
      <div v-else-if="hasData || isLoadingReport" class="space-y-4">
        <!-- Credit usage section -->
        <div v-if="hasCredits" class="rounded-lg border border-n-weak p-4">
          <h3 class="text-sm font-medium text-n-slate-12 mb-3">
            {{ t('CAMPAIGN.REPORT.CREDITS.TITLE') }}
          </h3>
          <div class="grid grid-cols-3 gap-4 text-center">
            <div>
              <p class="text-lg font-semibold text-n-slate-12">
                {{ creditInfo.reserved }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.REPORT.CREDITS.RESERVED') }}
              </p>
            </div>
            <div>
              <p class="text-lg font-semibold text-n-teal-11">
                {{ creditInfo.used }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.REPORT.CREDITS.USED') }}
              </p>
            </div>
            <div>
              <p class="text-lg font-semibold text-n-amber-11">
                {{ creditInfo.refunded }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.REPORT.CREDITS.REFUNDED') }}
              </p>
            </div>
          </div>
        </div>
        <!-- Delivery funnel stats -->
        <div
          v-if="stats && stats.total > 0"
          class="rounded-lg border border-n-weak p-4"
        >
          <h3 class="text-sm font-medium text-n-slate-12 mb-3">
            {{ t('CAMPAIGN.REPORT.STATS.TITLE') }}
          </h3>
          <div class="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-3">
            <div
              v-for="item in [
                {
                  key: 'pending',
                  label: t('CAMPAIGN.REPORT.STATS.PENDING'),
                  color: 'text-n-slate-11',
                },
                {
                  key: 'queued',
                  label: t('CAMPAIGN.REPORT.STATS.QUEUED'),
                  color: 'text-n-amber-11',
                },
                {
                  key: 'sent',
                  label: t('CAMPAIGN.REPORT.STATS.SENT'),
                  color: 'text-n-blue-11',
                },
                {
                  key: 'delivered',
                  label: t('CAMPAIGN.REPORT.STATS.DELIVERED'),
                  color: 'text-n-teal-11',
                },
                {
                  key: 'read',
                  label: t('CAMPAIGN.REPORT.STATS.READ'),
                  color: 'text-n-green-11',
                },
                {
                  key: 'failed',
                  label: t('CAMPAIGN.REPORT.STATS.FAILED'),
                  color: 'text-n-ruby-11',
                },
                {
                  key: 'skipped',
                  label: t('CAMPAIGN.REPORT.STATS.SKIPPED'),
                  color: 'text-n-amber-11',
                },
                {
                  key: 'replied',
                  label: t('CAMPAIGN.REPORT.STATS.REPLIED'),
                  color: 'text-n-violet-11',
                },
              ]"
              :key="item.key"
              class="text-center p-2 rounded-lg bg-n-alpha-2"
            >
              <p class="text-lg font-semibold" :class="item.color">
                {{ stats[item.key] || 0 }}
              </p>
              <p class="text-xs text-n-slate-10">{{ item.label }}</p>
            </div>
            <div class="text-center p-2 rounded-lg bg-n-alpha-2">
              <p class="text-lg font-semibold text-n-slate-12">
                {{ stats.delivery_rate || 0 }}%
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.REPORT.STATS.DELIVERY_RATE') }}
              </p>
            </div>
            <div class="text-center p-2 rounded-lg bg-n-alpha-2">
              <p class="text-lg font-semibold text-n-violet-11">
                {{ stats.reply_rate || 0 }}%
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.REPORT.STATS.REPLY_RATE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Resultados do Teste A/B -->
        <div
          v-if="abStats?.variants?.length"
          class="mb-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h3
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
            >
              <span class="i-lucide-flask-conical w-4 h-4 text-n-iris-11" />
              {{ t('CAMPAIGN.REPORT.AB.TITLE') }}
            </h3>
            <span
              v-if="abStats.winner"
              class="text-xs font-medium text-n-teal-11"
            >
              {{ t('CAMPAIGN.REPORT.AB.WINNER', { name: abStats.winner }) }}
            </span>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-n-slate-10">
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.VARIANT') }}
                  </th>
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.SENT') }}
                  </th>
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.DELIVERED') }}
                  </th>
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.READ') }}
                  </th>
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.REPLIED') }}
                  </th>
                  <th class="py-1.5 pr-3 font-medium">
                    {{ t('CAMPAIGN.REPORT.AB.REPLY_RATE') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="variantRow in abStats.variants"
                  :key="variantRow.name"
                  class="border-t border-n-weak text-n-slate-12"
                >
                  <td class="py-1.5 pr-3 font-mono text-xs">
                    {{ variantRow.name }}
                  </td>
                  <td class="py-1.5 pr-3 tabular-nums">
                    {{ variantRow.sent }}
                  </td>
                  <td class="py-1.5 pr-3 tabular-nums">
                    {{ variantRow.delivered }}
                  </td>
                  <td class="py-1.5 pr-3 tabular-nums">
                    {{ variantRow.read }}
                  </td>
                  <td class="py-1.5 pr-3 tabular-nums">
                    {{ variantRow.replied }}
                  </td>
                  <td class="py-1.5 pr-3 tabular-nums">
                    {{ variantRow.reply_rate || 0 }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="mt-2 text-[11px] text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.AB.HINT') }}
          </p>
        </div>

        <CampaignReport
          ref="campaignReportRef"
          :campaign="activeCampaign"
          :summary="summary"
          :contacts="contacts"
          :is-loading="isLoadingReport"
          @close="handleClose"
          @retry-contact="retryContact"
        />
      </div>
      <div
        v-else-if="showMissingState"
        class="flex justify-center py-10 text-sm text-n-slate-11"
      >
        {{ t('CAMPAIGN.REPORT.MISSING_CAMPAIGN') }}
      </div>
    </main>

    <!-- Retry confirmation dialog -->
    <Teleport to="body">
      <div
        v-if="showRetryConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black/50"
        @click.self="showRetryConfirm = false"
      >
        <div
          class="bg-n-solid-1 rounded-xl border border-n-weak shadow-xl w-full max-w-sm mx-4 p-6 space-y-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('CAMPAIGN.REPORT.RETRY.CONFIRM_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-10 mt-1">
              {{
                t('CAMPAIGN.REPORT.RETRY.CONFIRM_DESC', {
                  count: failedCount,
                })
              }}
            </p>
          </div>
          <div class="flex justify-end gap-2">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              @click="showRetryConfirm = false"
            >
              {{ t('CAMPAIGN.WIZARD.CANCEL') }}
            </Button>
            <Button size="sm" color="ruby" @click="retryFailed">
              <span class="i-lucide-refresh-cw w-3.5 h-3.5 mr-1" />
              {{ t('CAMPAIGN.REPORT.RETRY.BUTTON') }}
            </Button>
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>
